import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle, Phone, MapPin, Youtube } from 'lucide-react';
import { PageHeader, Button, Input } from '@/components/shared/ui';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/apiClient';
import FaqSection from './FaqSection';

const WHATSAPP_URL = 'https://wa.me/qr/MW7PEFBMFHTTB1';

interface InquiryFormState {
  organizationName: string;
  name: string;
  phone: string;
  email: string;
  message: string;
}

/**
 * 고객문의 페이지.
 * 기관명, 이름, 연락처, 이메일, 문의내용 입력 폼 제공.
 */
export default function ContactUs(): React.ReactElement {
  const { t, i18n } = useTranslation('menu');
  const { t: tFooter } = useTranslation('footer');
  const { user } = useAuth();
  const isKorean = i18n.language.toLowerCase().startsWith('ko');
  const [form, setForm] = useState<InquiryFormState>({
    organizationName: user?.currentAccount?.organizationName ?? '',
    name: user?.name ?? '',
    phone: '',
    email: user?.email ?? '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      organizationName: user?.currentAccount?.organizationName ?? prev.organizationName,
      name: user?.name ?? prev.name,
      email: user?.email ?? prev.email,
    }));
  }, [user?.currentAccount?.organizationName, user?.name, user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { organizationName, name, phone, email, message } = form;
    
    if (!organizationName.trim() || !name.trim() || !phone.trim() || !email.trim()) {
      alert(t('contactPage.requiredFields'));
      return;
    }

    if (!message.trim()) {
      alert(t('contactPage.messageRequired'));
      return;
    }

    try {
      setIsSubmitting(true);
      
      await apiClient.post('/contact', {
        organizationName: organizationName.trim(),
        name: name.trim(),
        phoneNumber: phone.trim(),
        email: email.trim(),
        content: message.trim(),
      }, { skipAuth: true });

      setForm({ 
        organizationName: user?.currentAccount?.organizationName ?? '', 
        name: user?.name ?? '', 
        phone: '', 
        email: user?.email ?? '', 
        message: '' 
      });
      alert(t('contactPage.submitSuccess'));
    } catch (error: any) {
      console.error('Contact submission error:', error);
      alert(error?.message || t('contactPage.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 py-8">
      <PageHeader
        title={t('contactPage.title')}
        description={
          <div className="w-full space-y-1">
            <p>{t('contactPage.description')}</p>
            <p>{t('contactPage.faqGuide')}</p>
          </div>
        }
      />

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 문의 폼 (좌측) */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('contactPage.formTitle')}
          </h3>
          <p className="text-gray-600 text-base mb-4">
            {t('contactPage.formGuide')}
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label={t('contactPage.formOrganization')}
              value={form.organizationName}
              onChange={(e) => setForm((f) => ({ ...f, organizationName: e.target.value }))}
              placeholder={t('contactPage.formOrganizationPlaceholder')}
              disabled={isSubmitting}
            />
            <Input
              label={t('contactPage.formName')}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t('contactPage.formNamePlaceholder')}
              disabled={isSubmitting}
            />
            <Input
              label={t('contactPage.formPhone')}
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder={t('contactPage.formPhonePlaceholder')}
              disabled={isSubmitting}
            />
            <Input
              label={t('contactPage.formEmail')}
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder={t('contactPage.formEmailPlaceholder')}
              disabled={isSubmitting}
            />
            <Input
              label={t('contactPage.formMessage')}
              multiline
              rows={4}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              placeholder={t('contactPage.formMessagePlaceholder')}
              disabled={isSubmitting}
            />
            <Button type="submit" variant="lightdark" size="md" disabled={isSubmitting}>
              {isSubmitting ? t('contactPage.submitting') : t('contactPage.submitButton')}
            </Button>
          </form>
        </div>

        {/* 연락처 안내 (우측) */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden flex flex-col h-full">
          <div className="p-6">
            <ul className="space-y-4">
              <li className="flex flex-wrap items-center gap-2">
                {isKorean ? (
                  <a
                    href="tel:02-564-8853"
                    className="inline-flex items-center justify-center gap-2 rounded-md font-medium h-[38px] px-4 py-3 text-base border border-brand-600/50 text-brand-600 hover:bg-brand-50 hover:text-brand-600 transition-colors focus:ring-1 focus:ring-offset-1 focus:ring-brand-50"
                  >
                    <Phone size={18} className="shrink-0" />
                    {t('contactPage.phoneNumber')}
                  </a>
                ) : (
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md font-medium h-[38px] px-4 py-3 text-base border border-green-600/50 text-green-600 hover:bg-green-50 hover:text-green-700 transition-colors focus:ring-1 focus:ring-offset-1 focus:ring-green-100"
                  >
                    <MessageCircle size={18} className="shrink-0" />
                    {tFooter('whatsapp')}
                  </a>
                )}
                <a
                  href="https://youtube.com/@newbase9254?si=KD7rk0OFcN_eW36k"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md font-medium h-[38px] px-4 py-2 text-sm border border-red-600/50 text-red-600 hover:bg-red-50 hover:text-red-600 transition-colors focus:ring-1 focus:ring-offset-1 focus:ring-red-50"
                >
                  <Youtube size={20} className="shrink-0" />
                  YouTube
                </a>
              </li>
              {isKorean && (
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <h4 className="text-base font-semibold text-gray-900 my-2 py-2">{t('contactPage.phoneHoursTitle')}</h4>
                  <p className="text-base text-gray-700">{t('contactPage.phoneHoursWeekdays')}</p>
                  <p className="text-base text-gray-600 mt-1">{t('contactPage.phoneHoursLunch')}</p>
                </div>
              )}
              <li className="flex items-start gap-3">
                <span className="text-gray-700 font-medium">{t('contactPage.companyName')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={20} className="text-gray-500 shrink-0 mt-0.5" />
                <a
                  href="mailto:contact@newbase.kr"
                  className="text-brand-600 hover:text-brand-700 hover:underline"
                >
                  {tFooter('email')}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-gray-500 shrink-0 mt-0.5" />
                <span className="text-gray-700 leading-relaxed">{tFooter('address')}</span>
              </li>
            </ul>
          </div>
          <div className="aspect-video w-full bg-gray-100 mt-auto shrink-0">
            <img
              src="/assets/images/office.png"
              alt="NEWBASE"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      <FaqSection />
    </div>
  );
}
